/**
 * Provider-agnostic service management hooks.
 * These three endpoints live on the generic /services/:id path and are shared
 * by compute, kubernetes, and any other console that needs rename / cancel /
 * delete without provider-specific logic.
 *
 *   PUT    /api/v3/services/:id          { service_name }  → rename
 *   POST   /api/v3/services/:id/cancel                     → archive subscription
 *   DELETE /api/v3/services/:id                            → soft-terminate
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

/** Invalidates both the general services list and the mine (useMyServices) cache. */
function invalidateServices(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['services'] });
  qc.invalidateQueries({ queryKey: ['my-services'] });
  // Also covers the paged service key shape used by useMyServices in services.ts
  qc.invalidateQueries({ queryKey: ['v3', 'services'] });
}

// ---------------------------------------------------------------------------
// Rename — PUT /api/v3/services/:id
// Body: { service_name: string }
// ---------------------------------------------------------------------------

export interface RenameServiceResult {
  id: string;
  service_name: string;
}

/**
 * Rename a service's display name. Works for compute, kubernetes, or any other
 * service type — calls the generic PUT /services/:id endpoint.
 */
export function useRenameService(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (serviceName: string): Promise<RenameServiceResult> => {
      const res = await serviceApiV3.put<{ data?: RenameServiceResult }>(
        `/services/${serviceId}`,
        { service_name: serviceName },
      );
      return res.data?.data ?? ({ id: serviceId, service_name: serviceName } as RenameServiceResult);
    },
    onSuccess: () => {
      invalidateServices(qc);
    },
  });
}

// ---------------------------------------------------------------------------
// Cancel subscription — POST /api/v3/services/:id/cancel
// No request body. Archives the legacy subscription + marks @mgmt archived.
// ---------------------------------------------------------------------------

export interface CancelServiceResult {
  id?: string;
  status?: string;
}

/**
 * Cancel a service subscription. Marks the service as archived on both the
 * legacy billing system and the @mgmt database. Blocked while active resources
 * still exist — the caller must gate this on the resource count.
 */
export function useCancelSubscription(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<CancelServiceResult> => {
      const res = await serviceApiV3.post<{ data?: CancelServiceResult }>(
        `/services/${serviceId}/cancel`,
      );
      return res.data?.data ?? {};
    },
    onSuccess: () => {
      invalidateServices(qc);
    },
  });
}

// ---------------------------------------------------------------------------
// Delete — DELETE /api/v3/services/:id
// Soft-terminates the service row (mirrors useDeleteWaapService from
// src/api/manticore/service-waap.ts). Blocked while active resources exist.
// ---------------------------------------------------------------------------

export interface DeleteServiceResult {
  service_name?: string;
}

/**
 * Soft-delete a service. Removes the service row and its provisioned zones;
 * the action is audit-logged on the backend. Blocked by active resources
 * (gate in the calling component, not here).
 */
export function useDeleteService(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<DeleteServiceResult> => {
      const res = await serviceApiV3.delete<{ data?: DeleteServiceResult }>(
        `/services/${serviceId}`,
      );
      return res.data?.data ?? {};
    },
    onSuccess: () => {
      invalidateServices(qc);
    },
  });
}
